import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  AssessmentOutlined,
  BuildOutlined,
  CalendarMonthOutlined,
  DownloadOutlined,
  HistoryOutlined,
  Inventory2Outlined,
  PaymentsOutlined,
  SwapHorizOutlined
} from '@mui/icons-material';
import { apiFetch, pobierzExcel } from '../services/api';

const raportyDefinicje = [
  {
    id:'pelny',
    bg:'#EAF2FF',
    fg:'#2457A6',
    nazwa:'Pełny raport ewidencji',
    opis:'Jeden plik Excel z osobnymi arkuszami: ewidencja, parametry techniczne, amortyzacja, harmonogram i przesunięcia.',
    endpoint:'/raporty/pelny.xlsx',
    plik:'pelny_raport_ewidencji.xlsx',
    icon:AssessmentOutlined,
    glowne:true
  },
  {
    id:'ewidencja',
    bg:'#EAF2FF',
    fg:'#2457A6',
    nazwa:'Ewidencja środków trwałych',
    opis:'Dane identyfikacyjne, KŚT, lokalizacja, przypisanie, status, daty, firma serwisowa, oprogramowanie i notatki.',
    endpoint:'/raporty/ewidencyjne.xlsx',
    plik:'raport_ewidencja_srodkow_trwalych.xlsx',
    icon:Inventory2Outlined
  },
  {
    id:'parametry',
    bg:'#EDF7EA',
    fg:'#3F7D32',
    nazwa:'Parametry techniczne',
    opis:'Parametry techniczne środków wraz z wartościami i jednostkami.',
    endpoint:'/raporty/parametry.xlsx',
    plik:'raport_parametry_techniczne.xlsx',
    icon:BuildOutlined
  },
  {
    id:'amortyzacja',
    bg:'#F3EEFA',
    fg:'#76529B',
    nazwa:'Amortyzacja',
    opis:'Metoda, stawki, wartość początkowa, bieżące umorzenie, wartość netto, daty i następny odpis.',
    endpoint:'/raporty/amortyzacja.xlsx',
    plik:'raport_amortyzacja.xlsx',
    icon:PaymentsOutlined
  },
  {
    id:'harmonogram',
    bg:'#FFF7E0',
    fg:'#A66F00',
    nazwa:'Harmonogram amortyzacji',
    opis:'Szczegółowy harmonogram odpisów miesiąc po miesiącu dla wszystkich środków z planem amortyzacji.',
    endpoint:'/raporty/harmonogram.xlsx',
    plik:'raport_harmonogram_amortyzacji.xlsx',
    icon:CalendarMonthOutlined
  },
  {
    id:'przesuniecia',
    bg:'#FFF0E6',
    fg:'#B85B17',
    nazwa:'Historia przesunięć',
    opis:'Zmiany lokalizacji i przypisania pracowników wraz z datą, powodem, notatką i wykonawcą.',
    endpoint:'/raporty/przesuniecia.xlsx',
    plik:'raport_historia_przesuniec.xlsx',
    icon:SwapHorizOutlined
  }
];

const typRaportu = {
  PELNY_RAPORT:'Pełny raport',
  PELNA_EWIDENCJA:'Ewidencja',
  DANE_EWIDENCYJNE:'Ewidencja',
  PARAMETRY_TECHNICZNE:'Parametry techniczne',
  AMORTYZACJA:'Amortyzacja',
  HARMONOGRAM_AMORTYZACJI:'Harmonogram amortyzacji',
  HISTORIA_PRZESUNIEC:'Historia przesunięć'
};

const dataPL = v => {
  if(!v) return '—';
  const d=new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('pl-PL');
};

export default function Raporty() {
  const [historia,setHistoria]=useState([]);
  const [blad,setBlad]=useState('');
  const [pobierany,setPobierany]=useState('');
  const [ladowanie,setLadowanie]=useState(true);
  const [stronaHistorii,setStronaHistorii]=useState(1);
  const naStroneHistorii=10;

  const load=async()=>{
    try{
      setLadowanie(true);
      const r=await apiFetch('/raporty');
      setHistoria(Array.isArray(r)?r:[]);
    }catch(e){
      setBlad(e?.message||'Nie udało się pobrać historii raportów.');
    }finally{
      setLadowanie(false);
    }
  };

  useEffect(()=>{ load(); },[]);

  const eksport=async(r)=>{
    try{
      setBlad('');
      setPobierany(r.id);
      await pobierzExcel(r.endpoint,r.plik);
      await load();
    }catch(e){
      setBlad(e?.message||'Nie udało się wygenerować raportu.');
    }finally{
      setPobierany('');
    }
  };

  const glowny=useMemo(()=>raportyDefinicje.find(x=>x.glowne),[]);
  const pojedyncze=useMemo(()=>raportyDefinicje.filter(x=>!x.glowne),[]);
  const liczbaStronHistorii=Math.max(1,Math.ceil(historia.length/naStroneHistorii));
  const historiaStrony=useMemo(()=>{
    const start=(stronaHistorii-1)*naStroneHistorii;
    return historia.slice(start,start+naStroneHistorii);
  },[historia,stronaHistorii]);

  useEffect(()=>{
    if(stronaHistorii>liczbaStronHistorii) setStronaHistorii(liczbaStronHistorii);
  },[liczbaStronHistorii,stronaHistorii]);

  return (
    <Box sx={{width:'100%',maxWidth:'none',mx:0,pb:4}}>
      <Box sx={{mb:2.5}}>
        <Typography variant="h4" fontWeight={900} sx={{color:'#172B4D'}}>
          Raporty i Excel
        </Typography>
        <Typography color="text.secondary" sx={{mt:.5,maxWidth:900}}>
          Generuj czytelne pliki Excel z danych zapisanych w systemie. Raporty zawierają wyłącznie dane użytkowe — bez technicznych identyfikatorów i pól systemowych.
        </Typography>
      </Box>

      {blad && <Alert severity="error" sx={{mb:2}} onClose={()=>setBlad('')}>{blad}</Alert>}

      <Paper
        sx={{
          p:{xs:2,md:2.5},
          mb:2.5,
          borderRadius:3,
          border:'1px solid #DDE6F0',
          boxShadow:'0 10px 30px rgba(20,48,83,.06)',
          background:'linear-gradient(135deg,#F7FAFF 0%,#FFFFFF 70%)'
        }}
      >
        <Stack direction={{xs:'column',md:'row'}} spacing={2} alignItems={{md:'center'}} justifyContent="space-between">
          <Box sx={{display:'flex',gap:1.7,alignItems:'flex-start'}}>
            <Box sx={{p:1.2,borderRadius:2,bgcolor:'#EAF2FF',color:'#2457A6',display:'flex'}}>
              <AssessmentOutlined/>
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight={900} color="#172B4D">
                  {glowny.nazwa}
                </Typography>
                <Chip label="Polecany" size="small" color="primary"/>
              </Stack>
              <Typography color="text.secondary" sx={{mt:.5,maxWidth:850}}>
                {glowny.opis}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={pobierany===glowny.id?<CircularProgress size={18} color="inherit"/>:<DownloadOutlined/>}
            disabled={Boolean(pobierany)}
            onClick={()=>eksport(glowny)}
            sx={{minWidth:205,py:1.15,fontWeight:800,textTransform:'none',borderRadius:2}}
          >
            Generuj pełny Excel
          </Button>
        </Stack>
      </Paper>

      <Typography variant="h6" fontWeight={900} color="#172B4D" sx={{mb:1.5}}>
        Raporty tematyczne
      </Typography>

      <Box sx={{
        display:'grid',
        gridTemplateColumns:{xs:'1fr',md:'repeat(2,minmax(0,1fr))'},
        gap:1.6,
        mb:3
      }}>
        {pojedyncze.map(r=>{
          const Icon=r.icon;
          return (
            <Paper
              key={r.id}
              variant="outlined"
              sx={{
                p:2,
                borderRadius:3,
                borderColor:'#DDE6F0',
                borderTop:`4px solid ${r.fg||'#355A86'}`,
                minHeight:210,
                display:'flex',
                flexDirection:'column'
              }}
            >
              <Box sx={{display:'flex',alignItems:'center',gap:1.2,mb:1.2}}>
                <Box sx={{p:1,borderRadius:2,bgcolor:r.bg||'#F2F6FB',color:r.fg||'#355A86',display:'flex'}}>
                  <Icon fontSize="small"/>
                </Box>
                <Typography fontWeight={900} color="#172B4D">{r.nazwa}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{lineHeight:1.55,mb:2,flex:1}}>
                {r.opis}
              </Typography>
              <Button
                variant="outlined"
                startIcon={pobierany===r.id?<CircularProgress size={17}/>:<DownloadOutlined/>}
                disabled={Boolean(pobierany)}
                onClick={()=>eksport(r)}
                sx={{alignSelf:'flex-start',textTransform:'none',fontWeight:800,borderRadius:2,borderColor:r.fg,color:r.fg}}
              >
                Generuj Excel
              </Button>
            </Paper>
          );
        })}
      </Box>

      <Paper sx={{borderRadius:3,border:'1px solid #DDE6F0',overflow:'hidden'}}>
        <Box sx={{p:2,borderBottom:'1px solid #E7EDF4',display:'flex',alignItems:'center',gap:1}}>
          <HistoryOutlined sx={{color:'#496987'}}/>
          <Box>
            <Typography fontWeight={900} color="#172B4D">Historia wygenerowanych raportów</Typography>
            <Typography variant="body2" color="text.secondary">
              Ostatnie eksporty wykonane w systemie.
            </Typography>
          </Box>
        </Box>

        {ladowanie ? (
          <Box sx={{p:4,textAlign:'center'}}><CircularProgress size={28}/></Box>
        ) : historia.length===0 ? (
          <Box sx={{p:4,textAlign:'center'}}>
            <Typography color="text.secondary">Nie wygenerowano jeszcze żadnego raportu.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{bgcolor:'#F7F9FC'}}>
                    <TableCell sx={{fontWeight:800}}>Data</TableCell>
                    <TableCell sx={{fontWeight:800}}>Nazwa</TableCell>
                    <TableCell sx={{fontWeight:800}}>Typ</TableCell>
                    <TableCell sx={{fontWeight:800}}>Format</TableCell>
                    <TableCell sx={{fontWeight:800}}>Rekordy</TableCell>
                    <TableCell sx={{fontWeight:800}}>Status</TableCell>
                    <TableCell sx={{fontWeight:800}}>Wygenerował</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historiaStrony.map(r=>(
                    <TableRow key={r.id_raportu} hover>
                      <TableCell>{dataPL(r.utworzono)}</TableCell>
                      <TableCell>{r.nazwa||'—'}</TableCell>
                      <TableCell>{typRaportu[r.typ]||r.typ||'—'}</TableCell>
                      <TableCell>{r.format||'XLSX'}</TableCell>
                      <TableCell>{r.liczba_rekordow ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status||'GOTOWY'}
                          size="small"
                          variant="outlined"
                          color={r.status==='GOTOWY'?'success':'default'}
                        />
                      </TableCell>
                      <TableCell>{r.wygenerowal_nazwa||'—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {historia.length>naStroneHistorii && (
              <Box sx={{p:2,display:'flex',justifyContent:'center',borderTop:'1px solid #E7EDF4'}}>
                <Pagination
                  count={liczbaStronHistorii}
                  page={stronaHistorii}
                  onChange={(_,page)=>setStronaHistorii(page)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
